import { useCallback, useMemo, useState } from "react";

import { Box, Stack, Typography, useTheme } from "@mui/material";
import { type EChartsOption } from "echarts";
import ECharts from "echarts-for-react";

import { useColors } from "../../../../CloudAnalytics/renderers/ECharts/useColors";
import { useWorkloadsMetrics } from "../../../hooks";
import { ChartId } from "../ClusterDetail";
import MetricTypeSelector, { type MetricType } from "../Components/MetricTypeSelector";
import { TimeRange } from "../Components/TimeRangeSelector";
import WorkloadSelector from "../Components/WorkloadSelector";
import { useClusterDetailDashboardContext } from "../context";
import ChartCard from "./ChartCard";
import {
  formatCpu,
  formatMemory,
  formatTimestamp,
  formatTimestampAxis,
  formatTimestampHourAxis,
  getCommonChartOptions,
} from "./utils";

const chartStyles: React.CSSProperties = {
  height: "100%",
  width: "100%",
};

const xAxisIntervals = 10;
const memoryUnit = "B";

interface MultiWorkloadChartProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEventHandlers?: (chartId: ChartId) => Record<string, (event: any) => void>;
  chartRef?: React.RefObject<ECharts>;
  clusterId: string;
}

const MultiWorkloadChart = ({ chartRef, getEventHandlers, clusterId }: MultiWorkloadChartProps) => {
  const { timeRange, percentile } = useClusterDetailDashboardContext();
  const theme = useTheme();

  const [selectedWorkloadIds, setSelectedWorkloadIds] = useState<string[]>([]);
  const [metricType, setMetricType] = useState<MetricType>("memory");

  const { workloads, loading } = useWorkloadsMetrics({
    timeRange,
    percentile,
    clusterId,
    workloadIds: selectedWorkloadIds,
  });

  const { dashboardWorkloads } = useClusterDetailDashboardContext();

  const workloadNameMap = useMemo(
    () =>
      dashboardWorkloads.reduce(
        (acc, workload) => {
          acc[workload.id] = workload.name;
          return acc;
        },
        {} as Record<string, string>
      ),
    [dashboardWorkloads]
  );

  const isDayGranularity = [TimeRange.T_14D, TimeRange.T_30D].includes(timeRange ?? TimeRange.T_30D);
  const isHourlyTimeRange = [TimeRange.T_1H, TimeRange.T_4H, TimeRange.T_12H, TimeRange.T_1D].includes(
    timeRange ?? TimeRange.T_30D
  );

  const xAxisLabelFormatter = useCallback(
    (value: string) => (isHourlyTimeRange ? formatTimestampHourAxis(value) : formatTimestampAxis(value)),
    [isHourlyTimeRange]
  );

  const { colors } = useColors(Object.keys(workloads).length, false);

  const metricWithLongestTimeRange = Object.values(workloads).reduce(
    (longest, current) => (current.length > longest.length ? current : longest),
    []
  );

  const baseMetrics = useMemo(
    () =>
      metricWithLongestTimeRange?.map((m) => ({
        ...m,
        formatted_start_time: isDayGranularity
          ? formatTimestampAxis(formatTimestamp(m.start_time))
          : formatTimestamp(m.start_time),
      })),
    [metricWithLongestTimeRange, isDayGranularity]
  );

  const showSymbol = baseMetrics.length <= 10;

  const chartOptions: EChartsOption = useMemo(
    () => ({
      ...getCommonChartOptions(theme, {
        grid: {
          top: 10,
          left: 100,
          right: 10,
          bottom: 50,
        },
        yAxis: {
          axisLabel: {
            formatter: (value) => (metricType === "memory" ? formatMemory(value, 4, memoryUnit) : formatCpu(value, 4)),
          },
        },
        tooltip: {
          valueFormatter(value) {
            if (metricType === "memory") {
              return formatMemory(String(value), 4, memoryUnit);
            } else {
              return formatCpu(String(value), 4);
            }
          },
        },
      }),
      color: colors,
      xAxis: {
        data: baseMetrics.map((m) => m.formatted_start_time),
        axisLabel: {
          formatter: xAxisLabelFormatter,
          interval: Math.floor(baseMetrics.length / xAxisIntervals),
        },
      },
      series: Object.entries(workloads).map(([id, data]) => ({
        name: workloadNameMap[id] || id,
        type: "line",
        data: data.map((m) => m[metricType === "memory" ? "memory" : "cpu"] || 0),
        connectNulls: true,
        showSymbol,
      })),
      legend: {
        textStyle: {
          color: theme.palette.text.primary,
        },
        bottom: 0,
        type: "scroll",
        icon: "circle",
        pageIconColor: theme.palette.text.primary,
        pageIconInactiveColor: theme.palette.general.divider,
      },
    }),
    [theme, colors, baseMetrics, xAxisLabelFormatter, workloads, metricType, showSymbol, workloadNameMap]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <ChartCard
        title="Workload utilization"
        height={300}
        loading={loading}
        rightHeaderContent={
          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <WorkloadSelector value={selectedWorkloadIds} onChange={setSelectedWorkloadIds} />
            <MetricTypeSelector value={metricType} onChange={setMetricType} />
          </Stack>
        }
      >
        {selectedWorkloadIds.length > 0 ? (
          <ECharts
            key={selectedWorkloadIds.length}
            style={chartStyles}
            option={chartOptions}
            ref={chartRef}
            onEvents={getEventHandlers?.(ChartId.WORKLOADS)}
            lazyUpdate
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Typography>Select workloads to see utilization</Typography>
          </Box>
        )}
      </ChartCard>
    </Box>
  );
};

export default MultiWorkloadChart;
